import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { Lora_700Bold } from '@expo-google-fonts/lora';
import { useAuth } from '../../lib/contexts/AuthContext';
import { DynamicTitle } from '../DynamicTitle';
import { registroDuplicadoMessage } from '../../lib/utils/registroErrors';

type Step = 1 | 2;

const STORAGE_VERIF_EMAIL = 'comensal_registro_verificacion_email';

function ssGet(key: string): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

function ssRemove(key: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

function stepLabel(step: Step): string {
  if (step === 1) return ' · Datos de acceso';
  return ' · Verifica tu correo';
}

export function ComensalRegistroPage() {
  const navigate = useNavigate();
  const [fontsLoaded] = useFonts({ Lora_700Bold });
  const { loading: authLoading, signUpComensal, signIn, user, resendSignupConfirmation } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailConfirmHint, setEmailConfirmHint] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      ssRemove(STORAGE_VERIF_EMAIL);
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (authLoading || user) return;
    const pending = ssGet(STORAGE_VERIF_EMAIL);
    if (pending) {
      setEmail(pending);
      setStep(2);
    }
  }, [authLoading, user]);

  const clearError = () => {
    if (formError) setFormError(null);
  };

  const handlePaso1 = async () => {
    setFormError(null);
    setEmailConfirmHint(null);

    if (!email.trim()) {
      setFormError('Indica tu correo electrónico');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setFormError('El correo no es válido');
      return;
    }
    if (!password || password.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Las contraseñas no coinciden');
      return;
    }

    const emailFmt = email.trim().toLowerCase();
    setLoading(true);
    try {
      const { session } = await signUpComensal(emailFmt, password);
      if (session?.user) {
        ssRemove(STORAGE_VERIF_EMAIL);
        navigate('/', { replace: true });
        return;
      }
      ssSet(STORAGE_VERIF_EMAIL, emailFmt);
      setStep(2);
    } catch (error: any) {
      const duplicado = registroDuplicadoMessage(error);
      if (duplicado) {
        setFormError(duplicado);
      } else {
        const msg = (error?.message ?? String(error ?? '')).toLowerCase();
        const rateLimited =
          msg.includes('rate limit') || msg.includes('email rate limit') || msg.includes('too many');
        if (rateLimited) {
          try {
            await signIn(emailFmt, password);
            ssRemove(STORAGE_VERIF_EMAIL);
            navigate('/', { replace: true });
          } catch {
            setFormError(null);
            ssSet(STORAGE_VERIF_EMAIL, emailFmt);
            setStep(2);
          }
        } else {
          setFormError(error?.message || 'No se pudo crear la cuenta. Intenta de nuevo.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    const e = email.trim().toLowerCase();
    if (!e) {
      setFormError('Falta el correo. Vuelve al paso anterior si hace falta.');
      return;
    }
    setFormError(null);
    setEmailConfirmHint(null);
    setLoading(true);
    try {
      await resendSignupConfirmation(e);
      setEmailConfirmHint(
        'Te hemos vuelto a enviar el correo. Abre el enlace en este dispositivo para entrar en la app.'
      );
    } catch (reErr: any) {
      setFormError(reErr?.message ?? 'No se pudo reenviar el correo.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !fontsLoaded) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#A20000" />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <DynamicTitle title="Registro — As Gastronómico" />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Crea tu cuenta</Text>
          <Text style={styles.subtitle}>
            Paso {step} de 2
            {stepLabel(step)}
          </Text>
        </View>

        {formError ? (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={20} color="#dc2626" style={styles.errorIcon} />
            <Text style={styles.errorText}>{formError}</Text>
          </View>
        ) : null}

        {emailConfirmHint ? (
          <View style={styles.infoBanner}>
            <Ionicons name="mail-outline" size={20} color="#0369a1" style={styles.errorIcon} />
            <Text style={styles.infoBannerText}>{emailConfirmHint}</Text>
          </View>
        ) : null}

        {step === 2 ? (
          <View style={styles.verifyBlock}>
            <View style={styles.infoBanner}>
              <Ionicons name="mail-outline" size={22} color="#4b5563" style={styles.errorIcon} />
              <Text style={styles.infoBannerTextMuted}>
                Te hemos enviado un correo con un enlace para confirmar tu cuenta. Revisa la bandeja de
                entrada y la carpeta de spam. Al pulsar el enlace se iniciará la sesión y entrarás en la
                aplicación.
              </Text>
            </View>
            <TouchableOpacity
              style={styles.resendButton}
              onPress={handleResendVerificationEmail}
              disabled={loading}
              activeOpacity={0.85}
            >
              <Text style={styles.resendButtonText}>Reenviar correo de verificación</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {step === 1 ? (
          <View style={styles.form}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Correo electrónico</Text>
              <View style={styles.inputContainer}>
                <Ionicons name="mail-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="tu@email.com"
                  placeholderTextColor="#9ca3af"
                  value={email}
                  onChangeText={(t) => {
                    setEmail(t);
                    clearError();
                  }}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  editable={!loading}
                />
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Contraseña</Text>
              <View style={styles.inputContainer}>
                <Ionicons name="lock-closed-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                <TextInput
                  style={[styles.input, styles.passwordInput]}
                  placeholder="Mínimo 6 caracteres"
                  placeholderTextColor="#9ca3af"
                  value={password}
                  onChangeText={(t) => {
                    setPassword(t);
                    clearError();
                  }}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!loading}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color="#6b7280" />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Confirmar contraseña</Text>
              <View style={styles.inputContainer}>
                <Ionicons name="lock-closed-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                <TextInput
                  style={styles.input}
                  placeholder="Repite la contraseña"
                  placeholderTextColor="#9ca3af"
                  value={confirmPassword}
                  onChangeText={(t) => {
                    setConfirmPassword(t);
                    clearError();
                  }}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!loading}
                />
              </View>
            </View>

            <TouchableOpacity
              style={[styles.primaryButton, loading && styles.primaryButtonDisabled]}
              onPress={handlePaso1}
              disabled={loading}
              activeOpacity={0.85}
            >
              {loading ? (
                <ActivityIndicator color="#ffffff" />
              ) : (
                <Text style={styles.primaryButtonText}>Continuar</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : null}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontFamily: 'Lora_700Bold',
    fontSize: 28,
    color: '#030213',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
  },
  errorIcon: {
    marginRight: 10,
    marginTop: 2,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#dc2626',
    fontWeight: '500',
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f0f9ff',
    borderWidth: 1,
    borderColor: '#bae6fd',
    borderRadius: 8,
    padding: 14,
    marginBottom: 16,
  },
  infoBannerText: {
    flex: 1,
    fontSize: 14,
    color: '#0c4a6e',
    fontWeight: '500',
    lineHeight: 20,
  },
  infoBannerTextMuted: {
    flex: 1,
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
  },
  verifyBlock: {
    marginBottom: 8,
  },
  resendButton: {
    alignSelf: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#A20000',
    backgroundColor: '#ffffff',
  },
  resendButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#A20000',
  },
  form: {
    width: '100%',
  },
  inputGroup: {
    marginBottom: 18,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  inputIcon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#030213',
    paddingVertical: 12,
    minWidth: 0,
  },
  passwordInput: {
    paddingRight: 36,
  },
  eyeIcon: {
    padding: 4,
    marginLeft: 4,
  },
  primaryButton: {
    backgroundColor: '#A20000',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  primaryButtonDisabled: {
    opacity: 0.65,
  },
  primaryButtonText: {
    fontFamily: 'Lora_700Bold',
    color: '#ffffff',
    fontSize: 16,
  },
});
